import React from 'react'
import {
  Box,
  Text,
  VStack,
  Heading,
  Link
} from '@chakra-ui/react'
import { FiExternalLink } from 'react-icons/fi'
import {
  CURRENT_TOS_VERSION,
  TOS_LAST_UPDATED,
  LEGAL_ENTITY_US,
  LEGAL_ENTITY_US_ADDRESS,
} from '../../config/legalVersions'

export { CURRENT_TOS_VERSION }

interface TermsOfServiceContentProps {
  /** Use compact styling for modal display (no header, less padding) */
  compact?: boolean
}

export const TermsOfServiceContent: React.FC<TermsOfServiceContentProps> = ({ compact = false }) => {
  const headingSize = compact ? 'md' : 'xl'
  const sectionGap = compact ? 4 : 6
  const sectionMt = compact ? 6 : 12
  
  return (
    <Box maxW={compact ? 'full' : '4xl'} mx="auto" p={compact ? 0 : 6}>
      <VStack gap={sectionGap} align="stretch">
        {/* Header - only show in full page mode */}
        {!compact && (
          <>
            <Box textAlign="center">
              <Heading size="3xl" fontWeight="800" mb={6}>
                Terms of Service
              </Heading>
              <Text color="gray.600" fontSize="md">
                Version {CURRENT_TOS_VERSION} • Last updated: {TOS_LAST_UPDATED}
              </Text>
            </Box>

            <Box height="1px" bg="gray.200" my={6} />

            {/* Table of Contents - only in full page mode */}
            <Box bg="blue.50" p={4} borderRadius="md" mb={6}>
              <Heading size="lg" fontWeight="700" mb={3} color="blue.700">Table of Contents</Heading>
              <Box as="ul" gap={1} fontSize="md" pl={4}>
                <Box as="li"><Link href="#acceptance" color="blue.600">1. Acceptance of Terms</Link></Box>
                <Box as="li"><Link href="#service-description" color="blue.600">2. Service Description & Scope</Link></Box>
                <Box as="li"><Link href="#subscription-billing" color="blue.600">3. Subscription Plans & Billing</Link></Box>
                <Box as="li"><Link href="#ai-services" color="blue.600">4. AI Services & Limitations</Link></Box>
                <Box as="li"><Link href="#external-ai-integrations" color="blue.600">5. External AI Integrations (MCP)</Link></Box>
                <Box as="li"><Link href="#genetic-data" color="blue.600">6. Genetic Data Rights & Privacy</Link></Box>
                <Box as="li"><Link href="#health-disclaimer" color="blue.600">7. Health Information Disclaimer</Link></Box>
                <Box as="li"><Link href="#profile-sharing" color="blue.600">8. Profile Sharing & Collaboration</Link></Box>
                <Box as="li"><Link href="#professional-services" color="blue.600">9. Professional Features</Link></Box>
                <Box as="li"><Link href="#third-party" color="blue.600">10. Third-Party Services</Link></Box>
                <Box as="li"><Link href="#data-processing" color="blue.600">11. Data Processing & Storage</Link></Box>
                <Box as="li"><Link href="#credit-usage" color="blue.600">12. Credit Usage & Metered Billing</Link></Box>
                <Box as="li"><Link href="#user-responsibilities" color="blue.600">13. User Responsibilities</Link></Box>
                <Box as="li"><Link href="#prohibited-uses" color="blue.600">14. Prohibited Uses</Link></Box>
                <Box as="li"><Link href="#intellectual-property" color="blue.600">15. Intellectual Property</Link></Box>
                <Box as="li"><Link href="#service-modifications" color="blue.600">16. Service Modifications & Termination</Link></Box>
                <Box as="li"><Link href="#limitation-liability" color="blue.600">17. Limitation of Liability</Link></Box>
                <Box as="li"><Link href="#indemnification" color="blue.600">18. Indemnification</Link></Box>
                <Box as="li"><Link href="#dispute-resolution" color="blue.600">19. Dispute Resolution</Link></Box>
                <Box as="li"><Link href="#compliance" color="blue.600">20. Privacy & Regulatory Commitments</Link></Box>
                <Box as="li"><Link href="#accessibility" color="blue.600">21. Accessibility & Non-Discrimination</Link></Box>
                <Box as="li"><Link href="#changes-terms" color="blue.600">22. Changes to Terms</Link></Box>
                <Box as="li"><Link href="#contact" color="blue.600">23. Contact & Legal Information</Link></Box>
                <Box as="li"><Link href="#affiliate-program" color="blue.600">24. Affiliate Referral Program</Link></Box>
              </Box>
            </Box>
          </>
        )}
        
        {/* Version info for compact mode */}
        {compact && (
          <Text color="gray.500" fontSize="sm" mb={2}>
            Version {CURRENT_TOS_VERSION} • Last updated: {TOS_LAST_UPDATED}
          </Text>
        )}

        {/* 1. Acceptance of Terms */}
        <Box id="acceptance" mt={compact ? 0 : sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>1. Acceptance of Terms</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">
            By accessing and using the Service operated by Livewello LLC (United States) and Livewello Ltd (United Kingdom) (collectively, "Livewello", "we", "us", or "our"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to abide by these terms, please do not use this service. Users based in the United Kingdom or European Union contract with Livewello Ltd; all other users contract with Livewello LLC.
          </Text>
          <Text mb={4} fontSize="md" lineHeight="tall">
            These Terms of Service constitute a legally binding agreement between you and LiveWello. By using our service, you represent that you have the legal capacity to enter into this agreement.
          </Text>
        </Box>

        {/* 2. Service Description & Scope */}
        <Box id="service-description" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>2. Service Description & Scope</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">
            Livewello is a comprehensive AI-powered wellness and genetic analysis platform that provides personalized health insights and wellness guidance. Our service includes:
          </Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Wellness Coach AI Assistant:</strong> Powered by OpenAI and Anthropic language models, providing personalized wellness guidance based on your genetic and health data</Box>
            <Box as="li"><strong>Genetic Data Processing:</strong> Analysis of genetic data from Livewello DNA kits, 23andMe, AncestryDNA, VCF files, and other genetic testing formats</Box>
            <Box as="li"><strong>Health Data Management:</strong> Comprehensive Personal Health Record (PHR) storage, document management, and wellness goal tracking</Box>
            <Box as="li"><strong>Profile Sharing & Collaboration:</strong> Role-based sharing of profiles with family members, healthcare providers, and wellness coaches</Box>
            <Box as="li"><strong>Professional Features:</strong> Advanced tools for healthcare practitioners and wellness professionals</Box>
            <Box as="li"><strong>Wellness Goals & Goals Partner:</strong> Goal setting, progress tracking, and Goals Partner support</Box>
            <Box as="li"><strong>External AI Integrations (MCP):</strong> Connect third-party AI assistants (e.g. Claude, ChatGPT) to your LiveWello data via the Model Context Protocol, OAuth, or API keys</Box>
          </Box>
        </Box>

        {/* 3. Subscription Plans & Billing */}
        <Box id="subscription-billing" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>3. Subscription Plans & Billing</Heading>
          
          <Text mb={4} fontWeight="700" fontSize="lg">Available Plans:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Free Plan:</strong> 1 profile, 150k conversation credits/month, 200MB storage, basic features</Box>
            <Box as="li"><strong>Basic Plan ($12.95/month):</strong> 3 profiles, 500k credits/month, 5GB storage, continuous AI coaching</Box>
            <Box as="li"><strong>Premium Plan ($25/month):</strong> 5 profiles, 1M credits/month, 10GB storage, advanced genetic insights</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">Billing Terms:</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li">Subscriptions automatically renew monthly unless cancelled</Box>
            <Box as="li">Payment processing handled by Stripe</Box>
            <Box as="li">Annual and 3-month billing options available (yearly: 2 months free; 3-month: one-time pass with no auto-renewal)</Box>
            <Box as="li">Pay-as-you-go add-ons available (credit packs, storage, additional profiles)</Box>
            <Box as="li">Legacy migrated users receive 6 months of legacy pricing</Box>
            <Box as="li">Subscription fees are generally non-refundable once a billing period begins, except where required by applicable law (including statutory withdrawal rights for qualifying EU/UK consumers)</Box>
            <Box as="li">One-time purchases (credit packs, storage add-ons) are non-refundable unless required by law or we expressly state otherwise at checkout</Box>
            <Box as="li">If you schedule account deletion, your subscription is cancelled immediately and no partial refund is issued for the current billing period</Box>
            <Box as="li">Price changes require 30 days notice (grandfathered users exempt)</Box>
          </Box>
        </Box>

        {/* 4. AI Services & Limitations */}
        <Box id="ai-services" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>4. AI Services & Limitations</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">
            Wellness Coach is powered by OpenAI and Anthropic language models. By using our AI services, you acknowledge:
          </Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li">AI responses are probabilistic and may contain errors or inaccuracies</Box>
            <Box as="li">AI is not a substitute for professional medical advice, diagnosis, or treatment</Box>
            <Box as="li">AI conversations consume credits based on length and complexity</Box>
            <Box as="li">When you upload health documents (lab reports, medical records), we automatically redact direct identifiers (name, SSN, MRN, DOB, address, phone, email) before AI analysis of those documents</Box>
            <Box as="li">When you converse with Wellness Coach, your messages and the health, genetic, and goal data needed to answer you are transmitted to our AI providers without additional redaction beyond what you choose to share</Box>
            <Box as="li">Our AI providers are contractually prohibited from using your data to train their public models</Box>
            <Box as="li">AI service availability depends on third-party providers (OpenAI, Anthropic)</Box>
            <Box as="li">You are responsible for verifying any AI-provided information</Box>
            <Box as="li">AI-generated content is subject to the terms of our AI service providers</Box>
          </Box>
        </Box>

        {/* 5. External AI Integrations (MCP) */}
        <Box id="external-ai-integrations" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>5. External AI Integrations (MCP)</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">
            LiveWello allows you to connect third-party AI assistants (such as Claude, ChatGPT, or other Model Context Protocol clients) to your LiveWello account. These integrations are distinct from Wellness Coach and operate under the following terms:
          </Text>

          <Text mb={3} fontWeight="700" fontSize="lg">How External AI Integrations Work:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>User-Initiated:</strong> You choose to connect an external AI assistant via Settings → AI Integrations or by approving an OAuth connection request</Box>
            <Box as="li"><strong>Authentication:</strong> Connections use OAuth 2.1 with PKCE or LiveWello MCP API keys that you generate</Box>
            <Box as="li"><strong>Scopes:</strong> You control the level of access granted — read-only (<em>livewello:read</em>) or read-write (<em>livewello:write</em>)</Box>
            <Box as="li"><strong>Revocable:</strong> You can disconnect any external AI assistant at any time from Account Settings → Integrations</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">Data Accessible to Connected AI Assistants:</Text>
          <Text mb={3} fontSize="md" lineHeight="tall">
            When you connect an external AI assistant, it may access the following data based on the scopes you grant:
          </Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">Your profiles (including shared profiles you have access to)</Box>
            <Box as="li">Health records, wellness goals, and progress data</Box>
            <Box as="li">Genetic summaries and SNP information</Box>
            <Box as="li">Uploaded documents and their content</Box>
            <Box as="li">Account preferences and session notes</Box>
            <Box as="li">With write access: the ability to create or update health records, wellness goals, and documents</Box>
          </Box>

          <Box bg="orange.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="orange.700" mb={2}>
              Important: External AI Assistants Are Not Controlled by LiveWello
            </Text>
            <Text fontSize="md" lineHeight="tall">
              When you connect an external AI assistant, your data is transmitted directly to that third-party service. <strong>LiveWello does not control how the external AI assistant processes, stores, or uses your data once it leaves our servers.</strong> The external AI assistant's own terms of service and privacy policy govern its handling of your information.
            </Text>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">Your Responsibilities:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">Review the privacy policy and terms of service of any AI assistant before connecting it</Box>
            <Box as="li">Grant only the minimum access level (scopes) needed</Box>
            <Box as="li">Keep your MCP API keys confidential and rotate them if compromised</Box>
            <Box as="li">Revoke access for any AI assistant you no longer use or trust</Box>
            <Box as="li">Understand that PII redaction applied to Wellness Coach does not apply to data accessed by external AI assistants via MCP</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">Rate Limits & Gating:</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li">External AI integrations are subject to rate limits based on your subscription plan</Box>
            <Box as="li">Certain features may require a paid subscription to access via MCP</Box>
            <Box as="li">Write operations are subject to hourly rate limits to protect data integrity</Box>
          </Box>
        </Box>

        {/* 6. Genetic Data Rights & Privacy */}
        <Box id="genetic-data" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>6. Genetic Data Rights & Privacy</Heading>
          <Text mb={4} fontWeight="700" fontSize="lg" color="green.600">
            🧬 YOU OWN YOUR GENETIC DATA - LiveWello is custodian only
          </Text>
          <Text mb={4} fontWeight="700" fontSize="lg" color="red.600">
            🚫 WE WILL NEVER SELL, RENT, OR COMMERCIALIZE YOUR GENETIC DATA
          </Text>
          
          <Text mb={4} fontSize="md" lineHeight="tall">Your genetic data rights and our commitments:</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Data Ownership:</strong> You retain full ownership of all genetic data you upload</Box>
            <Box as="li"><strong>Processing License:</strong> LiveWello has a limited license to process your data solely for service delivery</Box>
            <Box as="li"><strong>No Commercialization:</strong> We will never sell, rent, or commercialize your genetic information</Box>
            <Box as="li"><strong>File Size Limits:</strong> Genetic data files (including WGS/VCF files) have a maximum size limit of 2GB per file. Files larger than 2GB cannot be processed</Box>
            <Box as="li"><strong>File Count Limits:</strong> Genetic file upload limits vary by subscription plan. Free plans allow 1 genetic file per account. Paid plans (Basic, Premium) allow up to 3 genetic files per profile, subject to storage limits. Additionally, each profile can have a maximum of 1 VCF file or file 1GB or larger, regardless of subscription plan</Box>
            <Box as="li"><strong>Third-Party Sharing:</strong> Genetic data is only shared with:
              <Box as="ul" gap={1} pl={4} mt={2}>
                <Box as="li">Explicit user consent (profile sharing)</Box>
                <Box as="li">As required by law</Box>
                <Box as="li">Service providers under strict data processing agreements (OpenAI, Anthropic, Google Cloud)</Box>
              </Box>
            </Box>
            <Box as="li"><strong>Export Rights:</strong> You can export your genetic data in standard formats anytime</Box>
            <Box as="li"><strong>Deletion Rights:</strong> You can permanently delete your genetic data</Box>
            <Box as="li"><strong>PII Protection:</strong> Direct identifiers are redacted before AI analysis of uploaded documents; Wellness Coach conversations may include health and genetic context as described in Section 4</Box>
          </Box>
        </Box>

        {/* 7. Health Information Disclaimer */}
        <Box id="health-disclaimer" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>7. Health Information Disclaimer</Heading>
          <Text mb={4} fontWeight="700" fontSize="lg" color="red.600">
            ⚠️ IMPORTANT: LiveWello is NOT a medical device or diagnostic tool
          </Text>
          
          <Text mb={4} fontSize="md" lineHeight="tall">Our services are for informational and educational purposes only:</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Not FDA-Approved:</strong> LiveWello is not an FDA-approved medical device or diagnostic tool</Box>
            <Box as="li"><strong>No Medical Diagnosis:</strong> We do not diagnose, treat, cure, or prevent any disease</Box>
            <Box as="li"><strong>Educational Only:</strong> Genetic insights are educational and informational only</Box>
            <Box as="li"><strong>Professional Consultation Required:</strong> Always consult qualified healthcare professionals for medical concerns</Box>
            <Box as="li"><strong>No Doctor-Patient Relationship:</strong> Using our service does not create a doctor-patient relationship</Box>
            <Box as="li"><strong>Pharmacogenomic Limitations:</strong> Any historical drug-response content is deprecated and not offered as a current feature</Box>
            <Box as="li"><strong>Ancestry Estimates:</strong> Ancestry and trait predictions are estimates with inherent limitations</Box>
          </Box>
        </Box>

        {/* 8. Profile Sharing & Collaboration */}
        <Box id="profile-sharing" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>8. Profile Sharing & Collaboration</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">LiveWello enables secure profile sharing with role-based access control:</Text>
          
          <Text mb={3} fontWeight="700" fontSize="lg">Sharing Roles:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Owner:</strong> Full access including sharing and access management</Box>
            <Box as="li"><strong>Editor:</strong> Can view and edit profiles and reports</Box>
            <Box as="li"><strong>Viewer:</strong> Can view profiles and reports (read-only)</Box>
            <Box as="li"><strong>Restricted Viewer:</strong> Can view basic profile info only (no genetic reports)</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">Sharing Terms:</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li">Profile owner controls all sharing permissions</Box>
            <Box as="li">Shared access can be revoked at any time</Box>
            <Box as="li">Recipients have no billing responsibility for shared profiles</Box>
            <Box as="li">Profile sharing with granular permissions is available on all plans</Box>
            <Box as="li">Collaborators and recipients do not need a paid subscription</Box>
            <Box as="li">Goals Partner sharing available for wellness goals</Box>
            <Box as="li">Record-level sharing restrictions supported</Box>
            <Box as="li">Magic link sharing with automatic expiration</Box>
          </Box>
        </Box>

        {/* 9. Professional Features (not currently offered) */}
        <Box id="professional-services" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>9. Professional Features</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">
            Livewello may introduce tools for healthcare practitioners and wellness professionals in the future.
            Those features are not currently offered. When available, separate terms may apply.
          </Text>
        </Box>

        {/* 10. Third-Party Services */}
        <Box id="third-party" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>10. Third-Party Services</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">LiveWello integrates with the following third-party services:</Text>
          
          <Text mb={3} fontWeight="700" fontSize="lg">AI Services:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">OpenAI — Primary AI language model and vision analysis for Wellness Coach and document processing</Box>
            <Box as="li">Anthropic — Alternative AI language model for Wellness Coach</Box>
            <Box as="li">External AI assistants (e.g. Claude Desktop, ChatGPT) - User-connected via MCP/OAuth (see Section 5)</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">Infrastructure & Services:</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li">Stripe - Payment processing (subject to Stripe's terms)</Box>
            <Box as="li">SendGrid - Email delivery service</Box>
            <Box as="li">Twilio - SMS and WhatsApp message delivery for opted-in users (subject to Twilio's terms)</Box>
            <Box as="li">Meta Platforms (WhatsApp Business) - WhatsApp message delivery for opted-in users (subject to Meta's WhatsApp Business Terms and Privacy Policy)</Box>
            <Box as="li">Google Cloud Platform - Backend compute, infrastructure, and data storage</Box>
            <Box as="li">Firebase Hosting - Frontend hosting and CDN</Box>
            <Box as="li">Pinecone - Vector database for AI/ML</Box>
            <Box as="li">Firebase Auth - User authentication</Box>
          </Box>

          <Text mt={4} fontWeight="700" fontSize="lg">Important Disclaimers:</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li">LiveWello is not responsible for third-party service failures or outages</Box>
            <Box as="li">Third-party terms and privacy policies apply to their respective services</Box>
            <Box as="li">Service availability depends on third-party provider uptime</Box>
            <Box as="li">Data processing by third parties is subject to their respective terms</Box>
          </Box>
        </Box>

        {/* 11. Data Processing & Storage */}
        <Box id="data-processing" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>11. Data Processing & Storage</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">Technical details about how we process and store your data:</Text>
          
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Storage Infrastructure:</strong> Data stored in Google Cloud (Firestore, BigQuery, Cloud Storage)</Box>
            <Box as="li"><strong>Genetic Processing:</strong> Genetic data processed in BigQuery for analysis</Box>
            <Box as="li"><strong>File Size Limits:</strong> Genetic data files have a maximum size limit of 2GB per file. Files larger than 2GB cannot be uploaded or processed</Box>
            <Box as="li"><strong>File Count Limits:</strong> Genetic file upload limits vary by subscription plan. Free plans allow 1 genetic file per account. Paid plans (Basic, Premium) allow up to 3 genetic files per profile, subject to storage limits. Additionally, each profile can have a maximum of 1 VCF file or file 1GB or larger, regardless of subscription plan</Box>
            <Box as="li"><strong>PII Protection:</strong> Direct identifiers are redacted before AI analysis of uploaded documents; Wellness Coach conversations may include health and genetic context as described in Section 4</Box>
            <Box as="li"><strong>Encryption:</strong> Encryption for sensitive data in transit and at rest</Box>
            <Box as="li"><strong>Backups:</strong> Regular backups and disaster recovery procedures</Box>
            <Box as="li"><strong>Data Retention:</strong> 
              <Box as="ul" gap={1} pl={4} mt={2}>
                <Box as="li">Active accounts: Data retained indefinitely</Box>
                <Box as="li">Deleted accounts: Data deleted within 30 days</Box>
                <Box as="li">Legacy accounts: Retained for 1 year post-migration</Box>
              </Box>
            </Box>
            <Box as="li"><strong>Geographic Storage:</strong> Data primarily stored in United States data centers</Box>
          </Box>
        </Box>

        {/* 12. Conversation Credits & Metered Billing */}
        <Box id="credit-usage" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>12. Conversation Credits & Metered Billing</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">Understanding our conversation credit-based AI usage system:</Text>

          <Box mb={4} p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
            <Text fontSize="md" fontWeight="semibold" mb={2} color="gray.900">What are conversation credits?</Text>
            <Text fontSize="sm" color="gray.700" lineHeight="tall" mb={2}>
              Think of conversation credits like "AI interaction points." Every conversation with Wellness Coach uses credits based on what you write, what Wellness Coach writes back, the conversation history, and actions Wellness Coach takes.
            </Text>
            <Text fontSize="sm" color="gray.600" lineHeight="tall">
              Approximately 1 credit = 0.75 words, or about 1,000 credits = 750 words.
            </Text>
          </Box>

          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Credit Consumption:</strong> AI conversations consume credits based on length and complexity. Credits are flexible and can be used for Coach sessions, including but not limited to: genetic report conversations, wellness goal check-ins, wellness coaching sessions, general Wellness Coach conversations, section-specific chats, and LLM utilities (title generation, summarization, document classification).</Box>
            <Box as="li"><strong>Monthly Allowances:</strong> Plans include 150k to 1M+ conversation credits per month</Box>
            <Box as="li"><strong>Reset Schedule:</strong> Credit allowances reset on calendar month basis (UTC)</Box>
            <Box as="li"><strong>Overage Handling:</strong> Service throttling when credits are exhausted; additional credit packs available for purchase</Box>
            <Box as="li"><strong>Credit Packs:</strong> Additional conversation credits for the current monthly billing cycle (they do not roll over to future months)</Box>
            <Box as="li"><strong>No Refunds:</strong> Unused credits are not refundable</Box>
            <Box as="li"><strong>Usage Tracking:</strong> Credit consumption tracked and displayed in your account dashboard</Box>
          </Box>
        </Box>

        {/* 13. User Responsibilities */}
        <Box id="user-responsibilities" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>13. User Responsibilities</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">As a LiveWello user, you agree to:</Text>
          
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li">Provide accurate and complete health information</Box>
            <Box as="li">Maintain account security and not share credentials</Box>
            <Box as="li">Verify genetic data files belong to the correct individual</Box>
            <Box as="li">Ensure all genetic files in a profile are from the same person</Box>
            <Box as="li">Comply with applicable laws and regulations</Box>
            <Box as="li">Not use the service for illegal genetic discrimination</Box>
            <Box as="li">Professional users must maintain appropriate credentials and licenses</Box>
            <Box as="li">Report security breaches or data concerns immediately</Box>
            <Box as="li">Use the service only for lawful purposes</Box>
            <Box as="li">Respect intellectual property rights</Box>
            <Box as="li">Safeguard MCP API keys and revoke compromised keys immediately</Box>
            <Box as="li">Review third-party AI assistant policies before granting access to your data via MCP</Box>
          </Box>
        </Box>

        {/* 14. Prohibited Uses */}
        <Box id="prohibited-uses">
          <Heading size="md" mb={4}>14. Prohibited Uses</Heading>
          <Text mb={4}>You may not use LiveWello:</Text>
          
          <Box as="ul" gap={2} pl={6}>
            <Box as="li">To upload genetic data of others without explicit consent</Box>
            <Box as="li">For employment or insurance discrimination based on genetic information</Box>
            <Box as="li">To attempt to reverse-engineer AI models or algorithms</Box>
            <Box as="li">For automated data scraping or extraction</Box>
            <Box as="li">To share account credentials or credit allowances</Box>
            <Box as="li">To create fake profiles or impersonate others</Box>
            <Box as="li">To provide medical diagnoses or treatment recommendations</Box>
            <Box as="li">To circumvent usage limits or security measures</Box>
            <Box as="li">To share MCP API keys or OAuth tokens with unauthorized parties</Box>
            <Box as="li">For any unlawful purpose or to solicit unlawful acts</Box>
            <Box as="li">To violate intellectual property rights</Box>
            <Box as="li">To harass, abuse, or discriminate against others</Box>
            <Box as="li">To submit false or misleading information</Box>
          </Box>
        </Box>

        {/* 15. Intellectual Property */}
        <Box id="intellectual-property">
          <Heading size="md" mb={4}>15. Intellectual Property</Heading>
          <Text mb={4}>Ownership and licensing terms:</Text>
          
          <Box as="ul" gap={2} pl={6}>
            <Box as="li"><strong>LiveWello Ownership:</strong> We own the platform, software, reports, and content</Box>
            <Box as="li"><strong>User Data Ownership:</strong> You own your uploaded data (genetic, health records, documents)</Box>
            <Box as="li"><strong>Processing License:</strong> You grant LiveWello a license to process your data for service delivery</Box>
            <Box as="li"><strong>Custom Reports:</strong> Reports generated for you are your property</Box>
            <Box as="li"><strong>AI-Generated Content:</strong> You own AI-generated content, subject to AI provider terms</Box>
            <Box as="li"><strong>Trademarks:</strong> LiveWello trademarks and branding are our property</Box>
            <Box as="li"><strong>No Reverse Engineering:</strong> You may not reverse engineer our software or algorithms</Box>
          </Box>
        </Box>

        {/* 16. Service Modifications & Termination */}
        <Box id="service-modifications" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>16. Service Modifications & Termination</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">Service changes and account termination:</Text>
          
          <Text mb={3} fontWeight="700" fontSize="lg">Our Rights:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">Modify features with reasonable notice</Box>
            <Box as="li">Change pricing with 30 days notice (grandfathered users exempt)</Box>
            <Box as="li">Terminate accounts violating these terms</Box>
            <Box as="li">Suspend service for maintenance or updates</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">Your Rights:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">Cancel subscription anytime</Box>
            <Box as="li">Export your data before termination</Box>
            <Box as="li">Request account deletion (30-day grace period; you may cancel by signing in)</Box>
            <Box as="li">Terminate if you disagree with material changes</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">Data Handling:</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li">Data export available upon request</Box>
            <Box as="li">Account deletion: Scheduled with a 30-day grace period; permanent purge after grace period ends</Box>
            <Box as="li">Survival clauses: IP, liability, and indemnification terms survive termination. Our genetic data commitments in Section 6 — including our commitment never to sell, rent, or commercialize your genetic data, and the obligation of any successor to honor it — survive termination of these Terms, deletion of your account, and any transfer of our business, and continue to apply to any of your data that remains in our systems or a successor&apos;s</Box>
          </Box>
        </Box>

        {/* 17. Limitation of Liability */}
        <Box id="limitation-liability" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>17. Limitation of Liability</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">Limitations on our liability:</Text>
          
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>AI Errors:</strong> No liability for AI errors, inaccuracies, or hallucinations</Box>
            <Box as="li"><strong>Third-Party Failures:</strong> No liability for third-party service failures or outages</Box>
            <Box as="li"><strong>External AI Integrations:</strong> No liability for how third-party AI assistants connected via MCP process, store, or use your data</Box>
            <Box as="li"><strong>Genetic Interpretations:</strong> No liability for genetic interpretation errors</Box>
            <Box as="li"><strong>Health Decisions:</strong> No liability for health decisions based on our service</Box>
            <Box as="li"><strong>Data Breaches:</strong> No liability beyond statutory requirements</Box>
            <Box as="li"><strong>Damages Cap:</strong> Liability limited to subscription fees paid in the last 12 months (or $100 if you use the free plan)</Box>
            <Box as="li"><strong>Non-Excludable Liability:</strong> Nothing in these Terms limits liability for death or personal injury caused by negligence, fraud, willful misconduct, or any liability that cannot be limited under applicable law</Box>
            <Box as="li"><strong>Force Majeure:</strong> No liability for events beyond our control</Box>
            <Box as="li"><strong>Consequential Damages:</strong> No liability for indirect, incidental, or consequential damages</Box>
          </Box>
        </Box>

        {/* 18. Indemnification */}
        <Box id="indemnification" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>18. Indemnification</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">You agree to indemnify and hold harmless LiveWello from claims arising from:</Text>
          
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li">Your violation of these Terms of Service</Box>
            <Box as="li">Your infringement of intellectual property rights</Box>
            <Box as="li">Your illegal use of the service</Box>
            <Box as="li">Data you upload or share through the service</Box>
            <Box as="li">Professional users: Client claims and malpractice allegations</Box>
            <Box as="li">Your violation of applicable laws or regulations</Box>
          </Box>
        </Box>

        {/* 19. Dispute Resolution */}
        <Box id="dispute-resolution" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>19. Dispute Resolution</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">
            We prefer to resolve concerns informally. This section explains how disputes are handled and how mandatory consumer protections may apply.
          </Text>

          <Text mb={3} fontWeight="700" fontSize="lg">Informal Resolution First</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">Contact <Link href="mailto:support@livewello.com" color="blue.500">support@livewello.com</Link> and allow at least 30 days for us to try to resolve the issue before starting formal proceedings</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">Governing Law</Text>
          <Text mb={4} fontSize="md" lineHeight="tall">
            These Terms are governed by the laws of the State of South Carolina and applicable United States federal law, without regard to conflict-of-law rules. If you contract with Livewello Ltd (UK/EU users as described in Section 1), mandatory consumer protections in your country of residence still apply where they cannot be waived.
          </Text>

          <Text mb={3} fontWeight="700" fontSize="lg">Arbitration Agreement (US Users)</Text>
          <Text mb={3} fontSize="md" lineHeight="tall">
            If you reside in the United States, you and Livewello agree that disputes arising out of or relating to these Terms or the Service will be resolved by binding individual arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules, except as stated below.
          </Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Individual claims only:</strong> Arbitration is on an individual basis. Class actions, class arbitrations, and representative actions are not permitted</Box>
            <Box as="li"><strong>Small claims court:</strong> Either party may bring qualifying claims in small claims court in Charleston County, South Carolina, or your county of residence</Box>
            <Box as="li"><strong>Injunctive relief:</strong> Either party may seek temporary or preliminary injunctive relief in court to protect intellectual property or prevent unauthorized access</Box>
            <Box as="li"><strong>Arbitration costs:</strong> Each party pays its own attorneys&apos; fees unless the arbitrator awards fees to the prevailing party as permitted by law. AAA fees follow AAA rules; Livewello will not seek attorneys&apos; fees in arbitration unless your claim is frivolous or brought for an improper purpose</Box>
            <Box as="li"><strong>30-day opt-out:</strong> You may opt out of this arbitration agreement within 30 days of first accepting these Terms by emailing <Link href="mailto:support@livewello.com" color="blue.500">support@livewello.com</Link> with subject line &quot;Arbitration Opt-Out&quot; and the email address on your account. Opting out does not affect the rest of these Terms</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">UK, EU, and EEA Consumers</Text>
          <Text mb={4} fontSize="md" lineHeight="tall">
            If you are a consumer in the United Kingdom, European Union, or European Economic Area, you may bring proceedings in the courts of your country of residence. Nothing in this section removes mandatory consumer rights you have under local law, including rights to use local dispute-resolution bodies where available.
          </Text>

          <Text mb={3} fontWeight="700" fontSize="lg">Venue for Permitted Court Proceedings</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">For disputes that may be heard in court (including small claims and injunctive relief), the parties consent to the exclusive jurisdiction of state and federal courts located in Charleston County, South Carolina, unless mandatory consumer law requires a different forum</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">Severability</Text>
          <Text mb={4} fontSize="md" lineHeight="tall">
            If any part of this dispute resolution section is found unenforceable, that part will be severed and the remainder will continue in effect. If the class-action waiver is found unenforceable, the arbitration agreement will not apply to that claim and it must be litigated in court.
          </Text>
        </Box>

        {/* 20. Privacy & Regulatory Commitments */}
        <Box id="compliance" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>20. Privacy & Regulatory Commitments</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">
            Livewello is not a healthcare provider or health plan. Information you provide as a consumer is protected under this Privacy Policy, our contracts with service providers, and applicable privacy laws — not HIPAA, unless you separately enter into a covered-entity arrangement with us when such features are offered.
          </Text>
          
          <Text mb={3} fontWeight="700" fontSize="lg">Security & Privacy Safeguards:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">Administrative, physical, and technical safeguards designed to protect sensitive data</Box>
            <Box as="li">Breach notification procedures consistent with applicable law</Box>
            <Box as="li">Audit logging and access controls</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">GDPR Rights (EU Users):</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Right of Access:</strong> Request copies of your personal data</Box>
            <Box as="li"><strong>Right of Rectification:</strong> Correct inaccurate personal data</Box>
            <Box as="li"><strong>Right of Erasure:</strong> Request deletion of personal data</Box>
            <Box as="li"><strong>Right of Portability:</strong> Export your data in machine-readable format</Box>
            <Box as="li"><strong>Data Processing Agreements:</strong> Available upon request for service providers processing data on our behalf</Box>
            <Box as="li"><strong>Privacy Contact:</strong> support@livewello.com for privacy concerns</Box>
          </Box>
        </Box>

        {/* 21. Accessibility & Non-Discrimination */}
        <Box id="accessibility" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>21. Accessibility & Non-Discrimination</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">Our commitment to equal access:</Text>
          
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>WCAG 2.1 AA:</strong> We strive to meet Web Content Accessibility Guidelines</Box>
            <Box as="li"><strong>GINA Compliance:</strong> Genetic Information Nondiscrimination Act compliance</Box>
            <Box as="li"><strong>Equal Access:</strong> Service available regardless of genetic information</Box>
            <Box as="li"><strong>Disability Accommodations:</strong> Reasonable accommodations available upon request</Box>
            <Box as="li"><strong>Non-Discrimination:</strong> No discrimination based on genetic information</Box>
            <Box as="li"><strong>Accessibility Feedback:</strong> We welcome feedback on accessibility improvements</Box>
          </Box>
        </Box>

        {/* 22. Changes to Terms */}
        <Box id="changes-terms" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>22. Changes to Terms</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">How we handle changes to these terms:</Text>
          
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Material Changes:</strong> 30 days notice via email for material changes</Box>
            <Box as="li"><strong>Version Control:</strong> All changes numbered and dated</Box>
            <Box as="li"><strong>Continued Use:</strong> Continued use constitutes acceptance of changes</Box>
            <Box as="li"><strong>Right to Terminate:</strong> You may terminate if you disagree with changes</Box>
            <Box as="li"><strong>Archive:</strong> Previous TOS versions at <Link href="/terms/archive" color="blue.500">livewello.com/terms/archive</Link></Box>
            <Box as="li"><strong>Effective Date:</strong> Changes effective 30 days after notification</Box>
          </Box>
        </Box>

        {/* 23. Contact & Legal Information */}
        <Box id="contact" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>23. Contact & Legal Information</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">How to reach us:</Text>
          
          <Box bg="gray.50" p={4} borderRadius="md">
            <Text mb={2} fontSize="md" lineHeight="tall"><strong>Legal Entities:</strong> {LEGAL_ENTITY_US} ({LEGAL_ENTITY_US_ADDRESS}) and Livewello Ltd (United Kingdom)</Text>
            <Text mb={2} fontSize="md" lineHeight="tall"><strong>Contact:</strong> <Link href="mailto:support@livewello.com" color="blue.500">support@livewello.com</Link></Text>
            <Text mb={2} fontSize="md" lineHeight="tall"><strong>Website:</strong> <Link href="https://livewello.com/terms" color="blue.500" target="_blank" rel="noopener noreferrer">https://livewello.com/terms <FiExternalLink style={{ marginLeft: '2px' }} /></Link></Text>
          </Box>
        </Box>

        {/* 24. Affiliate Referral Program */}
        <Box id="affiliate-program" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>24. Affiliate Referral Program</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">
            Livewello offers an optional referral program that lets eligible users earn commissions when people they refer subscribe to paid plans. Participation is voluntary and subject to these terms in addition to the rest of this agreement.
          </Text>

          <Text mb={3} fontWeight="700" fontSize="lg">How It Works</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">You receive a personal referral link after enrolling in the program from your account settings</Box>
            <Box as="li">When a new user creates an account through your link and subscribes to a paid plan, you may earn a commission on qualifying subscription payments</Box>
            <Box as="li">The current commission rate is <strong>35%</strong> of each qualifying payment, for up to <strong>12 months</strong> from the referred user&apos;s account creation date, unless we change the program as described below</Box>
            <Box as="li">Commissions are paid via <strong>Stripe Connect Express</strong>. You must complete Stripe identity and payout onboarding before receiving payouts</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">Eligibility & Restrictions</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">You must be at least 18 years old and legally able to enter contracts in your jurisdiction</Box>
            <Box as="li">Self-referrals, duplicate accounts, fraudulent sign-ups, coupon abuse, or manipulation of tracking are prohibited and void commissions</Box>
            <Box as="li">You may not make false or misleading claims about Livewello, genetics, or health outcomes when promoting your link</Box>
            <Box as="li">We may withhold or reverse commissions for chargebacks, refunds, failed payments, or suspected fraud</Box>
            <Box as="li">Livewello may suspend or terminate your participation at any time for violation of these Terms or program abuse</Box>
          </Box>

          <Text mb={3} fontWeight="700" fontSize="lg">Taxes & Reporting</Text>
          <Text mb={4} fontSize="md" lineHeight="tall">
            You are responsible for any taxes owed on commissions. Stripe may collect tax information and issue US tax forms (such as 1099-K) when required by law. Livewello does not provide tax advice.
          </Text>

          <Text mb={3} fontWeight="700" fontSize="lg">Program Changes</Text>
          <Text mb={4} fontSize="md" lineHeight="tall">
            We may change commission rates, eligibility, payout timing, or discontinue the program with reasonable notice. Changes apply prospectively to new referrals and, where required by law, to existing participants.
          </Text>
        </Box>

        {!compact && <Box height="1px" bg="gray.200" my={6} />}

        <Box textAlign="center" color="gray.600" fontSize={compact ? 'sm' : 'md'} mt={compact ? 4 : 12}>
          <Text>
            By using LiveWello, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </Text>
        </Box>
      </VStack>
    </Box>
  )
}

export default TermsOfServiceContent
